// 3rd-party modules
import { Tooltip, message } from "antd";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// project modules
import Button from "../shared/button";
import Input, { Password } from "../shared/inputs/input";
import Loader from "../shared/loader";
import Select from "../shared/inputs/select";
import yup from "../../plugins/yup";
import { apiCall } from "../../helpers/apiHelper";
import { RootState } from "../../stores";
import { trimStringProps } from "../../helpers/objectHelper";

// apis
import * as AuthApi from '../../apis/authApi';
import * as CommonValueApi from '../../apis/commonValueApi';
import * as AccountLocationApi from '../../apis/accountLocationApi';

// models
import AccountLocationModal from "./locations/AccountLocationModal";
import ChangePasswordModel from "./ChangePasswordModel";
import ConfirmationPopup from "../shared/popup/confirmationPopup";
import ListView from "../shared/list/listView";
//import Switchable from "../shared/additionalComponents/Switchable";
import { Account } from "../../models/account";
import { AccountKiboshDevice } from "../../models/accountKiboshDevice";
import { AccountLocation } from "../../models/accountLocation";
import { accountRegisterViewModel } from "../../models/types/auth";
import { ApiResponse } from "../../models/response";
import { CommonValue } from "../../models/commonValue";
import { CONSTANTS } from "../../helpers/defines";
import { ListViewType } from "../shared/list/useListHook";
import { SvgHamburger } from "../svg";

type Props = {
  account?: Account;
  refresh?: boolean;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
};

const getAccountTypes = async (abortSignal?: AbortSignal) => {
  const response = await apiCall(CommonValueApi.getAccountTypes(abortSignal));

  return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
};

export default function AccountAddresses({ account, refresh, onCancel, onSave }: Props) {
  const kiboshDevice = useSelector((state: RootState) => state.user.kiboshDevice) || new AccountKiboshDevice();

  const [accountLocations, setAccountLocations] = useState<AccountLocation[]>([]);
  //const [accountTypes, setAccountTypes] = useState<CommonValue[]>([]);
  // const [accountStatus, setAccountStatus] = useState<string>("active");
  const [currentAccountLocation, setCurrentAccountLocation] = useState<AccountLocation>(new AccountLocation());
  // const [currentAccountStatus, setCurrentAccountStatus] = useState<string>("active");
  const [loading, setLoading] = useState(false);
  // const [loadingSave, setLoadingSave] = useState(false);
  //const [isAccountNameEditable, setIsAccountNameEditable] = useState(account?.accountTypeId === 33);
  const [isChangeBillingAdressConfirmationPopupOpen, setIsChangeBillingAdressConfirmationPopupOpen] = useState(false);
  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState(false);
  // const [isAccountStatusChangeConfirmationPopupOpen, setIsAccountStatusChangeConfirmationPopupOpen] = useState(false);
  //const [loadingAccountTypeList, setLoadingAccountTypeList] = useState(false);
  const [showAccountLocationModal, setShowAccountLocationModal] = useState(false);
  //const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const abortController = new AbortController();

  // useEffect(() => {
  //   if (account) {
  //     if (account.archived) {
  //       setAccountStatus("canceled");
  //     } else {
  //       if (account.lockoutEnabled) {
  //         setAccountStatus("inactive");
  //       } else {
  //         setAccountStatus("active");
  //       }
  //     }
  //   } else {
  //     setAccountStatus("active");
  //   }
  // }, [account]);


  useEffect(() => {

    if (account?.accountId) {
      getAccountLocations();
    }
  }, [account]);

  // const onCancelClick = () => {
  //   if (onCancel) {
  //     onCancel();
  //   }
  // }





  const getAccountLocations = async () => {
    setLoading(true);
    const response = await apiCall(AccountLocationApi.getAccountLocations(account?.accountId!, {}, abortController.signal));
    setLoading(false);
    setAccountLocations(response.success ? AccountLocation.toArrayOfClass(response.data?.value || []) : []);

    return response.success ? AccountLocation.toArrayOfClass(response.data?.value || []) : [];
  };

  const onAccountLocationSave = (e: AccountLocation) => {
    getAccountLocations();
  }

  // const onAccountStatusChange = (event: any, value: any) => {
  //   setCurrentAccountStatus(value);
  //   setIsAccountStatusChangeConfirmationPopupOpen(true);
  // }

  const onDeleteClick = (accountLocation: AccountLocation) => {
    setCurrentAccountLocation(accountLocation);
    setIsDeleteConfirmationPopupOpen(true);
  };

  const onEditClick = (accountLocation: AccountLocation) => {
    setCurrentAccountLocation({ ...accountLocation });
    setShowAccountLocationModal(true);
  };

  const onNewClick = () => {
    setCurrentAccountLocation(new AccountLocation());
    setShowAccountLocationModal(true);
  };

  const onSetAsBillingAddressClick = (accountLocation: AccountLocation) => {
    setCurrentAccountLocation(accountLocation);
    setIsChangeBillingAdressConfirmationPopupOpen(true);
  };

  const handleDeleteRequest = async () => {
    setLoading(true);
    const response = await apiCall(AccountLocationApi.deleteAccountLocation(currentAccountLocation));
    setLoading(false);

    if (response.success) {
      message.success(`Account Location deleted successfully.`);
      setIsDeleteConfirmationPopupOpen(false);
      getAccountLocations();
    }
  };



  const handleSetAsBillingAddressRequest = async () => {
    setLoading(true);
    const response = await apiCall(AccountLocationApi.setBillingAccountLocation(currentAccountLocation));
    setLoading(false);

    if (response.success) {
      message.success(`Account Billing Location changed successfully.`);
      setIsChangeBillingAdressConfirmationPopupOpen(false);
      getAccountLocations();
    }
  };

  const renderAddressSection = () => (
    <view
      // data-name="switchable"
      data-scroll=""
      data-space="20"
      data-gap="30"
    >
      <group data-gap="10" data-direction="column">
        <text data-weight="700" data-text-size="xx-large" data-wrap="wrap" data-color="main-dark">Addresses</text>
        <text data-wrap="wrap" data-length="300" data-line="1.5" data-light="">Here you can manage all your saved addresses, including adding new addresses, editing existing ones, and deleting outdated ones.</text>
      </group>
      <group data-width="auto" data-position="left">
        <Tooltip title="Create New Address" arrow={false}>
          <group data-width="auto">
            <Button
              secondary
              data-length="50"
              data-height="50"
              data-elevation="6"
              rounded
              icon="add"
              // text="New"
              onClick={onNewClick}
            />
          </group>
        </Tooltip>
      </group>
      <ListView
        dataSource={accountLocations}
        view={ListViewType.Block}
        keyField="accountLocationId"
        data-template="260"
        // scrollerProps={{ 'data-space': '20' }}
        listProps={{ 'data-gap': '10' }}
        itemComponent={BlockItem}
      />
    </view>
  )

  const BlockItem: React.FC<{ data: any }> = ({ data }) => {
    return (
      <group data-direction="column" data-border="" data-radius="10" data-contain="">
        <group
          data-space="30"
          data-gap="5"
        >
          <group>
            <text data-weight="700" data-color="main">{data.billingAddress ? "Billing " : ""}Address</text>
          </group>
          <text data-wrap="wrap">
            {[
              data.addressLine1,
              data.addressLine2,
              data.addressLine3,
              data.city,
              data.stateProvince,
              data.zipPostal,
              data.country
            ]
              .filter((x) => x)
              .join(", ")}
          </text>
        </group>
        <group data-gap="5" data-space="10" data-position="bottom" data-align="center" data-border="" data-wrap="no">
          <Button text="Set as Billing Addresss" disabled={data.billingAddress ?? null} onClick={() => onSetAsBillingAddressClick(data)} />
          <Tooltip title="Edit"><Button micro data-position="right" icon="edit" onClick={() => onEditClick(data)} /></Tooltip>
          <separator vertical=""></separator>
          <Tooltip title="Delete"><Button micro icon="delete" onClick={() => onDeleteClick(data)} /></Tooltip>
        </group>
      </group>
    )
  };

  return (
    <view data-vertical>
      {loading && <Loader />}
      {renderAddressSection()}
      {!!showAccountLocationModal && (
        <AccountLocationModal
          open={showAccountLocationModal}
          closeOnSave={true}
          account={account!}
          accountLocation={currentAccountLocation}
          shouldAddBilling={!accountLocations.filter(x => x.billingAddress).length}
          onClose={() => setShowAccountLocationModal(false)}
          onSave={(e) => onAccountLocationSave(e)}
        />
      )}
      {isDeleteConfirmationPopupOpen && (
        <ConfirmationPopup
          showButton={false}
          positiveButtonText="Delete"
          positiveCallback={handleDeleteRequest}
          negativeCallback={() => {
            setIsDeleteConfirmationPopupOpen(false);
          }}
        />
      )}
      {isChangeBillingAdressConfirmationPopupOpen && (
        <ConfirmationPopup
          showButton={false}
          positiveButtonText="Set as Billing Adress"
          positiveCallback={handleSetAsBillingAddressRequest}
          negativeCallback={() => {
            setIsChangeBillingAdressConfirmationPopupOpen(false);
          }}
        />
      )}

    </view>
  );
}
